/**
 * /leads: who booked a demo on timefly.no or registered in the app, and how far each got (owner,
 * 2026-09-26). Only the people in the server's LEADS_VIEWERS see it; everybody else is told so.
 * The statuses the app knows are read by the server when the list is asked for; the owner marks
 * contacted, customer or lost, and a note. Cards, not a table: the owner reads it on a phone.
 */
import { ApiError, api, errorText, get } from '@/api/client';
import { T } from '@/i18n';
import { SupportPanel } from '@/support/Support';
import { Button, Empty, LoadError, Panel, Skeleton, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export interface Lead {
  id: number;
  email: string;
  name: string | null;
  phone: string | null;
  company: string | null;
  message: string | null;
  source: 'skjema' | 'registrering';
  /** the source of the visit that made the lead, as the visit counts name it; null when not known */
  came_from: string | null;
  /** the ad (utm_content), when the link named one of the known tags */
  came_from_ad?: string | null;
  created_at: string;
  demo_opened_at: string | null;
  own_company: string | null;
  trial_ends_at: string | null;
  /** the trial company's staff and how many of them have logged in (#120); null without a company */
  staff?: number | null;
  staff_in?: number | null;
  contacted_at: string | null;
  outcome: 'kunde' | 'tapt' | null;
  note: string | null;
  status: string;
  /** the address is a user of a company that was there before the lead (issue #105): not a sign-up */
  existing_user?: { tenant_id: number; tenant_name: string } | null;
}

/** One visit to the campaign chat (issue #25, backend services/chatSessions.js). */
export interface ChatSession {
  id: string;
  started_at: string;
  source: Record<string, string>;
  device: Record<string, string | boolean>;
  steps: { t: number; step: string; text?: string; kind?: string }[];
  outcome: string | null;
  email: string | null;
  lead_id: number | null;
}
const STEP_WORDS: Record<string, string> = {
  opened: 'åpnet',
  show: 'Vis meg',
  not_now: 'Ikke nå',
  email: 'e-post',
  signin: 'innlogging',
  wrong_address: 'feil adresse',
  invalid_address: 'ugyldig adresse',
};
const OUTCOME_WORDS: Record<string, string> = {
  email_sent: 'sendte e-post',
  signin_google: 'Google',
  signin_microsoft: 'Microsoft',
  not_now: 'gikk etter Ikke nå',
  left: 'gikk',
};
/** "Ikke nå 2 s → «Hva koster det?» 3 s → e-post 7 s": what they did, in order, in seconds */
export const storyLine = (c: ChatSession) =>
  c.steps
    .filter((x) => x.step !== 'opened')
    .map(
      (x) =>
        `${x.step === 'question' ? `«${x.text ?? ''}»` : x.step === 'invalid_address' ? `«${x.text ?? ''}»` : (STEP_WORDS[x.step] ?? x.step)} ${x.t} s`,
    )
    .join(' → ');
const chatOrigin = (c: ChatSession) =>
  [
    c.source.utm_source,
    c.source.utm_content,
    c.device.in_app ? 'i Facebook-appen' : c.device.mobile ? 'mobil' : 'PC',
  ]
    .filter(Boolean)
    .join(' · ');

function Chats({ sessions }: { sessions: ChatSession[] }) {
  if (!sessions.length) return null;
  return (
    <Panel title={T('Kampanjechatten')} sub={T('siste 30 dager, hvert besøk med det de gjorde')}>
      {sessions.slice(0, 30).map((c) => (
        <div key={c.id} className="prow" data-testid="chat" style={{ display: 'block', padding: '8px 0' }}>
          <div>
            <b>
              {new Date(c.started_at).toLocaleString('nb-NO', {
                day: 'numeric',
                month: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/Oslo',
              })}
            </b>{' '}
            <span className="pv">{`${chatOrigin(c)} · ${T(OUTCOME_WORDS[c.outcome ?? ''] ?? 'pågår')}${c.email ? ` · ${c.email}` : ''}`}</span>
          </div>
          <div className="pv">{storyLine(c) || T('åpnet og gikk')}</div>
        </div>
      ))}
    </Panel>
  );
}

const STATUSES = ['Alle', 'Ny', 'Registrert', 'Demo åpnet', 'Kontaktet', 'Prøveperiode', 'Kunde', 'Tapt'];
const day = (iso: string | null) => (iso ? fmtD(iso.slice(0, 10)) : '');

function Card({ lead, chat }: { lead: Lead; chat?: ChatSession | undefined }) {
  const qc = useQueryClient();
  const [note, setNote] = useState(lead.note ?? '');
  const mark = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/leads/${lead.id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
    onError: (e) => toast(errorText(e)),
  });
  const facts = [
    lead.source === 'skjema'
      ? `${T('Skjema')} ${day(lead.created_at)}`
      : `${T('Registrert')} ${day(lead.created_at)}`,
    lead.came_from
      ? `${T('Fra')} ${lead.came_from}${lead.came_from_ad ? ` · ${lead.came_from_ad}` : ''}`
      : null,
    lead.demo_opened_at ? `${T('Demo')} ${day(lead.demo_opened_at)}` : null,
    lead.own_company ? `${lead.own_company}, ${T('prøveperiode til')} ${day(lead.trial_ends_at)}` : null,
    lead.own_company && lead.staff != null
      ? T('{i} av {n} ansatte har logget inn', { i: lead.staff_in ?? 0, n: lead.staff })
      : null,
    lead.contacted_at ? `${T('Kontaktet')} ${day(lead.contacted_at)}` : null,
  ].filter(Boolean);
  return (
    <div className="prow" data-testid="lead" style={{ display: 'block', padding: '12px 0' }}>
      <div>
        <b>{lead.name || lead.email}</b> <span className="pv">{lead.status}</span>
        {lead.existing_user ? (
          <span className="pv" data-testid="existing-user" style={{ marginLeft: 6 }}>
            {`${T('Ansatt hos')} ${lead.existing_user.tenant_name}`}
          </span>
        ) : null}
      </div>
      <div>
        <a href={`mailto:${lead.email}`}>{lead.email}</a>
        {lead.phone ? (
          <>
            {' · '}
            <a href={`tel:${lead.phone}`}>{lead.phone}</a>
          </>
        ) : null}
        {lead.company ? ` · ${lead.company}` : ''}
      </div>
      <div className="pv">{facts.join(' · ')}</div>
      {chat ? (
        <div className="pv" data-testid="lead-chat">{`${T('Chatten')}: ${storyLine(chat)}`}</div>
      ) : null}
      {lead.message ? <p style={{ margin: '6px 0', whiteSpace: 'pre-wrap' }}>{lead.message}</p> : null}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        <Button small onClick={() => mark.mutate({ contacted: !lead.contacted_at })}>
          {lead.contacted_at ? T('Ikke kontaktet') : T('Kontaktet')}
        </Button>
        <Button small onClick={() => mark.mutate({ outcome: lead.outcome === 'kunde' ? null : 'kunde' })}>
          {lead.outcome === 'kunde' ? T('Ikke kunde') : T('Kunde')}
        </Button>
        <Button small onClick={() => mark.mutate({ outcome: lead.outcome === 'tapt' ? null : 'tapt' })}>
          {lead.outcome === 'tapt' ? T('Ikke tapt') : T('Tapt')}
        </Button>
      </div>
      <textarea
        aria-label={`${T('Notat for')} ${lead.email}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => note !== (lead.note ?? '') && mark.mutate({ note })}
        rows={2}
        placeholder={T('Notat')}
        style={{ width: '100%', marginTop: 6 }}
      />
    </div>
  );
}

interface FunnelRow {
  day: string;
  step: string;
  source: string;
  device: string;
  n: number;
}

// the server's steps (services/funnel.js), said the way the owner reads them
const STEP_TEXT: Record<string, string> = {
  site_view: 'Besøk på timefly.no',
  site_cta_demo: 'Trykket «Prøv demoen»',
  site_cta_signup: 'Trykket «Kom i gang»',
  site_cta_walkthrough: 'Trykket «Book en gjennomgang»',
  book_view: 'Åpnet bookingsiden',
  help_view: 'Åpnet en hjelpeside',
  news_view: 'Åpnet nyhetssiden',
  lp_view: 'Åpnet kampanjesiden',
  lp_cta_demo: 'Trykket «Prøv demoen» på kampanjesiden',
  chat_view: 'Åpnet kampanjechatten',
  chat_show: 'Trykket «Vis meg»',
  chat_not_now: 'Trykket «Ikke nå»',
  chat_question: 'Stilte et spørsmål',
  chat_email: 'Sendte e-post fra chatten',
  chat_signin: 'Trykket Google eller Microsoft',
  form_view: 'Så demoskjemaet',
  form_typed: 'Begynte å skrive',
  link_sent: 'Demolenke sendt',
  oauth_signin: 'Logget inn med Google eller Microsoft',
  link_refused: 'Skjemaet avvist',
  link_page: 'Åpnet lenken fra e-posten',
  open_refused: 'Åpning avvist',
  demo_opened: 'Demoen åpnet',
};

const osloDay = (offset: number) =>
  new Date(Date.now() - offset * 86_400_000).toLocaleDateString('sv-SE', { timeZone: 'Europe/Oslo' });

interface FunnelReport {
  steps: string[];
  /** the app pages the server counts (2026-10-13), in the order it lists them */
  appPages?: string[];
  rows: FunnelRow[];
  // what visitors look at (owner 2026-10-03): the server's allowlist, backend services/funnel.js PAGES
  // the front page comes once per design (review 2026-10-12): `step` is the names' prefix, and its rows
  // are the version's from `from` (null: not live yet) and before `until`
  pages?: Record<
    string,
    {
      view: string;
      sec: string[];
      click: string[];
      step?: string;
      version?: string;
      from?: string | null;
      until?: string | null;
    }
  >;
  times?: string[];
}
const useFunnel = () =>
  useQuery({
    queryKey: ['leads', 'funnel'],
    queryFn: () => get<FunnelReport>('/leads/funnel'),
    retry: false,
  });

const PAGE_TEXT: Record<string, string> = {
  main: 'Forsiden, timefly.no',
  prov: 'Kampanjesiden, timefly.no/prov',
  book: 'Bookingsiden, timefly.no/book',
  help: 'Hjelpesidene, timefly.no/hjelp',
  news: 'Nyhetssiden, timefly.no/nyheter',
};
const TIME_TEXT: Record<string, string> = {
  '0-5s': 'under 5 s',
  '5-30s': '5 til 30 s',
  '30s-2m': '30 s til 2 min',
  '2m+': 'over 2 min',
};
const pct = (n: number, of: number) => (of ? `${n} · ${Math.round((100 * n) / of)} %` : String(n));

/** Sections seen, time on the page and clicks, per page, for one day or all 14 and one source or all. */
function PageMeasures() {
  const q = useFunnel();
  const [day, setDay] = useState('');
  const [source, setSource] = useState('');
  if (!q.data?.pages) return null;
  const { pages, times = [] } = q.data;
  const rows = q.data.rows.filter((r) => (!day || r.day === day) && (!source || r.source === source));
  const days = [...new Set(q.data.rows.map((r) => r.day))].sort().reverse();
  const sources = [...new Set(q.data.rows.map((r) => r.source))].sort();
  const cell = { textAlign: 'right' as const, padding: '2px 6px' };
  const table = (title: string, list: [string, string][]) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
      <thead>
        <tr className="pv">
          <th style={{ textAlign: 'left' }}>{title}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {list.map(([name, value]) => (
          <tr key={name}>
            <td>{name}</td>
            <td style={cell}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Panel
      title={T('Hva besøkende ser på')}
      sub={T('seksjoner sett minst halvveis i ett sekund, tid på siden og klikk, uten informasjonskapsler')}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select aria-label="Dag" value={day} onChange={(e) => setDay(e.target.value)}>
          <option value="">{T('Alle dager')}</option>
          {days.map((d) => (
            <option key={d} value={d}>
              {fmtD(d)}
            </option>
          ))}
        </select>
        <select aria-label="Kilde" value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">{T('Alle kilder')}</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
        {Object.entries(pages).map(([key, p]) => {
          const page = p.step ?? key;
          const live = (d: string) =>
            (p.from === undefined || (p.from !== null && d >= p.from)) && (!p.until || d < p.until);
          const sum = (step: string) =>
            rows.filter((r) => r.step === step && live(r.day)).reduce((a, r) => a + r.n, 0);
          const views = sum(p.view);
          // an earlier design with no visits in the range was not live then: not a row of zeros
          if (p.until !== undefined && !views) return null;
          const left = times.reduce((a, t) => a + sum(`${page}:time:${t}`), 0);
          const clicks = p.click
            .map((c) => [c, sum(`${page}:click:${c}`)] as const)
            .filter(([, n]) => n)
            .sort((a, b) => b[1] - a[1]);
          return (
            <div key={key} data-testid={`measure-${key}`} style={{ marginTop: 12 }}>
              <b>{T(PAGE_TEXT[page] ?? page)}</b>
              {p.version && <span className="pv">{` · ${T('design')} ${fmtD(p.version)}`}</span>}{' '}
              <span className="pv">{`${views} ${T('besøk')}`}</span>
              {views ? (
                <>
                  {table(
                    T('Seksjon, sett av'),
                    p.sec.map((s) => [s, pct(sum(`${page}:sec:${s}`), views)]),
                  )}
                  {table(
                    T('Tid på siden'),
                    times.map((t) => [T(TIME_TEXT[t] ?? t), pct(sum(`${page}:time:${t}`), left)]),
                  )}
                  {table(
                    T('Klikk'),
                    clicks.length ? clicks.map(([c, n]) => [c, String(n)]) : [[T('ingen'), '']],
                  )}
                </>
              ) : (
                <p className="pv">{T('Ingen tall ennå.')}</p>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/** Counts without cookies: every visitor, as a number per day; nothing about the person. */
function Funnel() {
  const q = useFunnel();
  if (!q.data?.rows) return null;
  const { steps, rows } = q.data;
  const today = osloDay(0);
  const yesterday = osloDay(1);
  const week = osloDay(6);
  const sum = (step: string, from: string, to = today, pick: (r: FunnelRow) => boolean = () => true) =>
    rows
      .filter((r) => r.step === step && r.day >= from && r.day <= to && pick(r))
      .reduce((a, r) => a + r.n, 0);
  const sources = [...new Set(rows.filter((r) => r.step === 'site_view').map((r) => r.source))];
  // which ad brought the visit (utm_content, rows `ad:<tag>`, 2026-10-04)
  const ads = [...new Set(rows.filter((r) => r.step.startsWith('ad:')).map((r) => r.step.slice(3)))];
  const cell = { textAlign: 'right' as const, padding: '2px 6px' };
  return (
    <Panel title={T('Besøk og demoer')} sub={T('alle besøkende telles, uten informasjonskapsler')}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="funnel">
        <thead>
          <tr className="pv">
            <th style={{ textAlign: 'left' }} />
            <th style={cell}>{T('I dag')}</th>
            <th style={cell}>{T('I går')}</th>
            <th style={cell}>{T('7 dager')}</th>
            <th style={cell}>{T('14 dager')}</th>
            <th style={cell}>{T('mobil')}</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((st) => {
            const all = sum(st, '0000');
            const mob = sum(st, '0000', today, (r) => r.device === 'mobil');
            return (
              <tr key={st}>
                <td>{T(STEP_TEXT[st] ?? st)}</td>
                <td style={cell}>{sum(st, today)}</td>
                <td style={cell}>{sum(st, yesterday, yesterday)}</td>
                <td style={cell}>{sum(st, week)}</td>
                <td style={cell}>{all}</td>
                <td style={cell}>{all ? `${Math.round((100 * mob) / all)} %` : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sources.length ? (
        <p className="pv" style={{ marginTop: 8 }}>
          {T('Besøk etter kilde, 14 dager')}:{' '}
          {sources
            .map((so) => `${so} ${sum('site_view', '0000', today, (r) => r.source === so)}`)
            .join(' · ')}
        </p>
      ) : null}
      {ads.length ? (
        <p className="pv" style={{ marginTop: 4 }}>
          {T('Besøk etter annonse, 14 dager')}: {ads.map((a) => `${a} ${sum(`ad:${a}`, '0000')}`).join(' · ')}
        </p>
      ) : null}
    </Panel>
  );
}

// the app's pages, said the way the owner reads them (backend APP_PAGES)
const APP_PAGE_TEXT: Record<string, string> = {
  hjem: 'Hjem',
  oversikt: 'Oversikt',
  dagen: 'Dagen',
  vaktplan: 'Vaktplan',
  'vaktplan-gammel': 'Vaktplan (gammel)',
  timeliste: 'Kontroll',
  arbeidstid: 'Registreringer',
  lonn: 'Rapporter',
  omsetning: 'Lønnsprosent',
  fravaer: 'Fravær',
  ansatte: 'Ansatte',
  prosjekter: 'Prosjekter',
  innstillinger: 'Innstillinger',
  varsler: 'Varsler',
  innboks: 'Innboks',
  lagvakt: 'Lagvakt',
  min: 'Min side',
  oppsett: 'Oppsett',
  hugin: 'Hugin',
};

/** Which pages are opened in the app, without cookies (owner 2026-10-13): per page, by company kind and device. */
function AppPages() {
  const q = useFunnel();
  const rows = q.data?.rows?.filter((r) => r.step.startsWith('app:')) ?? [];
  if (!rows.length) return null;
  const pages = q.data?.appPages ?? [...new Set(rows.map((r) => r.step.slice(4)))];
  const today = osloDay(0);
  const week = osloDay(6);
  const sum = (page: string, from: string, pick: (r: FunnelRow) => boolean = () => true) =>
    rows.filter((r) => r.step === `app:${page}` && r.day >= from && pick(r)).reduce((a, r) => a + r.n, 0);
  const cell = { textAlign: 'right' as const, padding: '2px 6px' };
  return (
    <Panel title={T('Sider i appen')} sub={T('hver side telles én gang per besøk, uten informasjonskapsler')}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="app-pages">
        <thead>
          <tr className="pv">
            <th style={{ textAlign: 'left' }} />
            <th style={cell}>{T('I dag')}</th>
            <th style={cell}>{T('7 dager')}</th>
            <th style={cell}>{T('14 dager')}</th>
            <th style={cell}>{T('bedrift')}</th>
            <th style={cell}>{T('demo')}</th>
            <th style={cell}>{T('mobil')}</th>
          </tr>
        </thead>
        <tbody>
          {pages
            .filter((pg) => sum(pg, '0000'))
            .map((pg) => {
              const all = sum(pg, '0000');
              const mob = sum(pg, '0000', (r) => r.device === 'mobil');
              return (
                <tr key={pg}>
                  <td>{T(APP_PAGE_TEXT[pg] ?? pg)}</td>
                  <td style={cell}>{sum(pg, today)}</td>
                  <td style={cell}>{sum(pg, week)}</td>
                  <td style={cell}>{all}</td>
                  <td style={cell}>{sum(pg, '0000', (r) => r.source === 'bedrift')}</td>
                  <td style={cell}>{sum(pg, '0000', (r) => r.source === 'demo')}</td>
                  <td style={cell}>{all ? `${Math.round((100 * mob) / all)} %` : ''}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </Panel>
  );
}

export default function Leads() {
  const [filter, setFilter] = useState('Alle');
  const q = useQuery({ queryKey: ['leads'], queryFn: () => get<{ leads: Lead[] }>('/leads'), retry: false });
  const chats = useQuery({
    queryKey: ['leads', 'chats'],
    queryFn: () => get<{ sessions: ChatSession[] }>('/leads/chats'),
    retry: false,
  });
  const sessions = chats.data?.sessions ?? [];
  // a lead's newest chat, by its lead id
  const chatOf = (id: number) => sessions.find((c) => c.lead_id === id);
  const all = q.data?.leads ?? [];
  const shown = filter === 'Alle' ? all : all.filter((l) => l.status === filter);
  // the totals are sign-ups: a customer's own user trying the product is shown, not counted (#105)
  const signups = all.filter((l) => !l.existing_user);
  const count = (s: string) => (s === 'Alle' ? signups.length : signups.filter((l) => l.status === s).length);
  const existing = all.length - signups.length;

  if (q.error instanceof ApiError && q.error.status === 403) {
    return (
      <div className="start">
        <Empty>{T('Denne siden er bare for Timefly.')}</Empty>
      </div>
    );
  }
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 16 }}>
      <SupportPanel />
      <Funnel />
      <AppPages />
      <PageMeasures />
      <Chats sessions={sessions} />
      <Panel title={T('Leads')} sub={T('demoforespørsler og registreringer')}>
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <LoadError what={T('leadsene')} onRetry={() => q.refetch()} />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  small
                  {...(s === filter ? { tone: 'pri' as const } : {})}
                  onClick={() => setFilter(s)}
                >
                  {`${T(s)} ${count(s)}`}
                </Button>
              ))}
            </div>
            {existing ? (
              <p className="pv" style={{ margin: '0 0 8px' }}>
                {`${existing} ${T('er ansatt i en bedrift som allerede er kunde, og telles ikke')}`}
              </p>
            ) : null}
            {shown.length ? (
              shown.map((l) => <Card key={l.id} lead={l} chat={chatOf(l.id)} />)
            ) : (
              <Empty>{T('Ingen her ennå.')}</Empty>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}
